import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import { focusCity } from "src/store/ui"
import { selectGame, selectIsReviewing, selectViewerId } from "src/store/game"
import { cellDisplayName } from "src/utils/maps"
import { playerFacingError } from "src/utils/errorMessages"
import { TRAINING_GAME_ID } from "src/tutorial/scenario"

import "./RoundOrders.css"

/* The orders of this round that cost an action and can still be taken back (owner, 2026-10-05:
   "We should be able to cancel the movements that are done this round (to recover actions from).
   Applies also for silo missiles and tunnels. I don't know where to cancel the actions from, up to
   you to decide").

   One list in the dock, above the roster of cities: one row per action spent — a march ordered
   this round, a missile in the air, a tunnel entry or exit, a colony ordered. Nothing on it has happened yet — the round turning is what makes
   them happen — so the ✕ is a clean take-back: the server gives back the action, and the march its
   units and its road's food, the silo its load (`cancel_movement`, `cancel_strike`,
   `cancel_tunnel_order`, `cancel_founding`). A march of an earlier round is on the road and is not
   listed. A level is not here: it costs no action, and is taken back on the building's tile. */

const bodies = (units) =>
  Object.values(units || {}).reduce((sum, unit) => sum + Number(typeof unit === "number" ? unit : unit?.number ?? 0), 0)

const placeOf = ([column, row] = []) => `${cellDisplayName({ column, row })} ${column}-${row}`

/* Pure, for the test: the viewer's cancellable orders, in the order they are listed. */
export const roundOrdersOf = ({ game, userId }) => {
  const metadata = game?.metadata
  if (!metadata || userId == null) return []
  const mine = (order) => String(order?.user_id) === String(userId)
  const marches = (metadata.movements || [])
    .filter(
      (m) => mine(m) && Number(m.created_tick) === Number(game.tick) && !m.halted && Number(m.current_index ?? 0) === 0
    )
    .map((m) => ({
      kind: "march",
      key: `march-${m.id}`,
      focus: m.path?.[0],
      count: bodies(m.units),
      from: m.path?.[0],
      to: m.path?.[m.path.length - 1],
      send: { action: "cancel_movement", movement_id: m.id },
    }))
  const strikes = (metadata.strikes || []).filter(mine).map((s) => ({
    kind: "strike",
    key: `strike-${s.origin}-${s.target}`,
    focus: s.target,
    to: s.target,
    send: {
      action: "cancel_strike",
      origin: { column: s.origin[0], row: s.origin[1] },
      target: { column: s.target[0], row: s.target[1] },
    },
  }))
  const tunnels = (metadata.tunnel_orders || []).filter(mine).map((o) => ({
    kind: o.direction === "unload" ? "tunnelOut" : "tunnelIn",
    key: `tunnel-${o.id}`,
    focus: o.coordinates,
    count: bodies(o.units),
    at: o.coordinates,
    send: { action: "cancel_tunnel_order", order_id: o.id },
  }))
  const colonies = (metadata.foundings || []).filter(mine).map((f) => ({
    kind: "colony",
    key: `colony-${f.coordinates}`,
    focus: f.coordinates,
    at: f.coordinates,
    send: { action: "cancel_founding", column: f.coordinates[0], row: f.coordinates[1] },
  }))
  return [...marches, ...strikes, ...tunnels, ...colonies]
}

const RoundOrders = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const game = useSelector(selectGame)
  const userId = useSelector(selectViewerId)
  const reviewing = useSelector(selectIsReviewing)

  if (reviewing || String(game?.id) === String(TRAINING_GAME_ID)) return null
  const orders = roundOrdersOf({ game, userId })
  if (orders.length === 0) return null

  // The server refuses any order, a take-back included, from a player who has readied up.
  const isReady = Boolean(game?.players?.find((seat) => String(seat.user.id) === String(userId))?.ready)
  const readyReason = isReady ? playerFacingError("UserAlreadyReady") : null

  const label = (order) => {
    if (order.kind === "march") return t("game.orders.march", { count: order.count, from: placeOf(order.from), to: placeOf(order.to) })
    if (order.kind === "strike") return t("game.orders.strike", { to: placeOf(order.to) })
    if (order.kind === "colony") return t("game.orders.colony", { at: placeOf(order.at) })
    return t(`game.orders.${order.kind}`, { count: order.count, at: placeOf(order.at) })
  }

  return (
    <section className="round-orders" data-testid="round-orders">
      <div className="section-header round-orders-head">
        <span className="p-small">{t("game.orders.title")}</span>
        <span>{orders.length}</span>
      </div>
      <p className="round-orders-hint">{t("game.orders.hint")}</p>
      <ul className="round-orders-list">
        {orders.map((order) => (
          <li key={order.key} className={`round-order ${order.kind}`}>
            <button
              type="button"
              className="round-order-label"
              onClick={() => order.focus && dispatch(focusCity({ col: order.focus[0], row: order.focus[1] }))}
            >
              {label(order)}
            </button>
            <button
              type="button"
              className="round-order-cancel"
              data-action="cancel-order"
              disabled={Boolean(readyReason)}
              title={readyReason || t("game.orders.cancel")}
              aria-label={t("game.orders.cancel")}
              onClick={() => wsClient.send("game", order.send)}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default RoundOrders
